import React from "react";
import { Box, Typography } from "@mui/material";

import { round } from "utils";
import { SubCategoryMap } from "components/PerformanceReview/constants";

/**
 * Renders the detail chip(s) for a selected sub-feature — mobile port of desktop
 * `ParameterTag`. Word lists (fillers, jargon, informal/rare words) render as
 * "word x count" chips (or "No word present!"); numeric metrics (pauses,
 * disfluency, pace, energy, confidence) render as a labelled chip.
 */

const COLORS = {
  good: { color: "#00664A", bg: "#DCFCE7" },
  avg: { color: "#BF7900", bg: "#FFF6E5" },
  low: { color: "#C51407", bg: "#FFE6E3" },
};

function TagChip({ color, bg, children }) {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 2,
        py: 1,
        borderRadius: 1,
        bgcolor: bg,
        color,
        width: "fit-content",
      }}
    >
      <Typography variant="body02-semiBold" color="inherit">
        {children}
      </Typography>
    </Box>
  );
}

function ArrayTag({ value = [], subCategory }) {
  const positive = ["RARE_USED_WORDS", "JARGON_WORDS"].includes(subCategory);
  const c = positive ? COLORS.good : COLORS.low;

  if (!value.length) {
    const emptyStyle =
      subCategory === SubCategoryMap.INFORMAL_WORDS
        ? COLORS.good
        : { color: "#111519", bg: "#F0F5FA" };
    return <TagChip color={emptyStyle.color} bg={emptyStyle.bg}>No word present!</TagChip>;
  }

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
      {value.map((v, i) => (
        <TagChip key={i} color={c.color} bg={c.bg}>
          {v.word} x {v.count}
        </TagChip>
      ))}
    </Box>
  );
}

function NumberTag({ value, subCategory, total }) {
  const v = round(value);
  let color = COLORS.low.color;
  let bg = COLORS.low.bg;
  let label = "NA";

  switch (subCategory) {
    case "PAUSES":
      label = `Pauses encountered : ${total}`;
      break;
    case "DISFLUENCY":
      label = `Breakdowns encountered : ${v}`;
      if (v === 0) ({ color, bg } = { color: COLORS.good.color, bg: COLORS.good.bg });
      break;
    case "PACE":
      if (v > 150) label = "😖 Your Speaking Pace was High";
      else if (v >= 100) {
        label = "😀 Your Speaking Pace was Ideal";
        ({ color, bg } = { color: COLORS.good.color, bg: COLORS.good.bg });
      } else {
        label = "😓 Your Speaking Pace was Low";
        ({ color, bg } = { color: COLORS.avg.color, bg: COLORS.avg.bg });
      }
      break;
    case "ENERGY":
      if (v < 1.5) {
        label = "😓 Your Speaking Energy was Low";
        ({ color, bg } = { color: COLORS.avg.color, bg: COLORS.avg.bg });
      } else {
        label = "😀 Your Speaking Energy was Ideal";
        ({ color, bg } = { color: COLORS.good.color, bg: COLORS.good.bg });
      }
      break;
    case "CONFIDENCE":
      if (v < 1.5) {
        label = "😓 You seemed underconfident while responding to the question";
        ({ color, bg } = { color: COLORS.avg.color, bg: COLORS.avg.bg });
      } else {
        label = "😀 Good confidence observed. keep honing it to reach your fullest potential";
        ({ color, bg } = { color: COLORS.good.color, bg: COLORS.good.bg });
      }
      break;
    default:
      return null;
  }

  return (
    <Box sx={{ mt: 2 }}>
      <TagChip color={color} bg={bg}>
        {label}
      </TagChip>
    </Box>
  );
}

export default function ParameterTag({ tag }) {
  const { value, subCategory, total } = tag || {};

  if (Array.isArray(value)) {
    return <ArrayTag value={value} subCategory={subCategory} />;
  }
  if (Number.isInteger(value)) {
    return <NumberTag value={value} subCategory={subCategory} total={total} />;
  }
  return null;
}
